/**
 * BillPrintView -- the printable itemized APPOINTMENT BILL
 * (BILLING-MISSING-DELTA-V1; tracker row "Print", OPEN on both sheets).
 *
 * The BILL half of the print family beside ReceiptPrintView (which stays
 * the PAYMENT receipt). Renders an ALREADY-READ bill through the shared
 * PrintSheet rail: no money write; only explicit audit actions (the print-never-repeats-money law,
 * R-G / FS-29). Money prints FULL PRECISION through THE ONE formatter --
 * compact notation never appears on a print surface.
 *
 * A CANCELLED bill prints with its status named in the title subs -- a
 * cancelled financial document is never silently reprinted as live.
 */

import PrintSheet from '@/dashboard/ui/PrintSheet';
import BillDetailCard from '@/dashboard/billing/BillDetailCard';
import { fetchBillingDocumentActivity, recordBillingPrintRequest } from '@/dashboard/api';
import { formatIstDate } from '@/lib/datetime-ist';
import {
  BILL_STATUS_LABELS,
  type PatientBillWireMirror,
} from '@/dashboard/billing/bill-view';

export interface BillPrintViewProps {
  readonly bill: PatientBillWireMirror;
  /** The clinic display label (caller-resolved from its own lawful wire). */
  readonly clinicLabel: string;
  readonly backLabel: string;
  readonly onBack: () => void;
}

export default function BillPrintView({
  bill,
  clinicLabel,
  backLabel,
  onBack,
}: BillPrintViewProps): JSX.Element {
  return (
    <PrintSheet
      title={`Bill #${bill.id}`}
      subs={[
        clinicLabel,
        `${BILL_STATUS_LABELS[bill.status]} - ${
          bill.patientName ?? 'Patient record'
        } - Appointment #${bill.appointmentId}`,
        `Generated ${formatIstDate(new Date().toISOString())}`,
      ]}
      backLabel={backLabel}
      onBack={onBack}
      testId="bill-print"
      printIdentity={`bill:${bill.clinicId}:${bill.id}`}
      onLoadActivity={() => fetchBillingDocumentActivity({ kind: 'bill', documentId: bill.id, clinicId: bill.clinicId })}
      onBeforePrint={() => recordBillingPrintRequest({ kind: 'bill', documentId: bill.id, clinicId: bill.clinicId })}
    >
      {/* forPrint: the treatment name must reach the paper whole -- paper has
          no hover to recover an ellipsis (BillDetailCard header). */}
      <BillDetailCard bill={bill} testId="bill-print-detail" forPrint documentLayout clinicLabel={clinicLabel} />
    </PrintSheet>
  );
}
